import { useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import Button from "../ui/Button";

function normalizeOptions(options = []) {
  return options.map((opt) =>
    typeof opt === "object" && opt !== null ? opt : { value: opt, label: String(opt) }
  );
}

function MultiSelect({ options, value, onChange }) {
  const selected = value ?? [];
  const toggle = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((v) => v !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      {normalizeOptions(options).map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
              active
                ? "bg-primary text-white shadow-sm"
                : "bg-primary/[0.04] text-primary/60 ring-1 ring-primary/[0.08] hover:text-primary"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function RangeInput({ value, onChange, min, max }) {
  const range = value ?? { min: "", max: "" };
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="block">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-primary/40">Min</span>
        <input
          type="number"
          min={min}
          max={max}
          value={range.min}
          onChange={(e) => onChange({ ...range, min: e.target.value })}
          placeholder={min != null ? String(min) : "0"}
          className="w-full rounded-lg border border-primary/10 px-3 py-2 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-primary/40">Max</span>
        <input
          type="number"
          min={min}
          max={max}
          value={range.max}
          onChange={(e) => onChange({ ...range, max: e.target.value })}
          placeholder={max != null ? String(max) : "Any"}
          className="w-full rounded-lg border border-primary/10 px-3 py-2 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </label>
    </div>
  );
}

function DateRangeInput({ value, onChange }) {
  const range = value ?? { from: "", to: "" };
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="block">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-primary/40">From</span>
        <input
          type="date"
          value={range.from}
          onChange={(e) => onChange({ ...range, from: e.target.value })}
          className="w-full rounded-lg border border-primary/10 px-3 py-2 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-primary/40">To</span>
        <input
          type="date"
          value={range.to}
          onChange={(e) => onChange({ ...range, to: e.target.value })}
          className="w-full rounded-lg border border-primary/10 px-3 py-2 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </label>
    </div>
  );
}

function FilterField({ field, value, onChange, options }) {
  switch (field.type) {
    case "multi":
      return <MultiSelect options={options} value={value} onChange={onChange} />;
    case "select":
    case "boolean":
      return (
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-primary/10 px-3 py-2.5 text-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
        >
          {normalizeOptions(
            field.type === "boolean"
              ? [
                  { value: "", label: "Any" },
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]
              : options
          ).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case "range":
      return <RangeInput value={value} onChange={onChange} min={field.min} max={field.max} />;
    case "dateRange":
      return <DateRangeInput value={value} onChange={onChange} />;
    default:
      return null;
  }
}

export default function TableFilterDrawer({
  open,
  onClose,
  config,
  filters,
  setFilter,
  resetFilters,
  fieldOptions,
  activeCount,
  filteredCount,
  totalCount,
}) {
  const sections = useMemo(() => {
    const map = new Map();
    for (const field of config.fields ?? []) {
      const section = field.section ?? "Filters";
      if (!map.has(section)) map.set(section, []);
      map.get(section).push(field);
    }
    return [...map.entries()];
  }, [config.fields]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="absolute inset-0 bg-primary-dark/45 backdrop-blur-[2px]"
            aria-label="Close filters"
            onClick={onClose}
          />

          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 340, mass: 0.85 }}
            className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
            role="dialog"
            aria-labelledby="filter-drawer-title"
          >
        <div className="flex items-start justify-between gap-3 border-b border-primary/[0.08] bg-gradient-to-r from-primary/[0.03] to-accent/[0.05] px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/[0.08] text-primary">
                <SlidersHorizontal className="h-4 w-4" />
              </span>
              <div>
                <h2 id="filter-drawer-title" className="text-base font-bold text-primary">
                  {config.title ?? "Filter & metrics"}
                </h2>
                <p className="text-xs text-primary/45">
                  {filteredCount.toLocaleString()} of {totalCount.toLocaleString()} records
                  {activeCount > 0 ? ` · ${activeCount} filter${activeCount === 1 ? "" : "s"} active` : ""}
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-primary/10 text-primary/50 hover:bg-primary/[0.04] hover:text-primary"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {sections.map(([section, fields]) => (
            <section key={section} className="mb-6 last:mb-0">
              <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-primary/40">
                {section}
              </h3>
              <div className="space-y-4">
                {fields.map((field) => (
                  <div key={field.key}>
                    <label className="mb-2 block text-xs font-semibold text-primary/70">
                      {field.label}
                    </label>
                    <FilterField
                      field={field}
                      value={filters[field.key]}
                      onChange={(val) => setFilter(field.key, val)}
                      options={fieldOptions[field.key] ?? field.options ?? []}
                    />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-primary/[0.08] bg-primary/[0.02] px-5 py-4">
          <Button variant="ghost" size="sm" onClick={resetFilters} disabled={activeCount === 0}>
            Clear all
          </Button>
          <Button size="sm" onClick={onClose}>
            Show {filteredCount.toLocaleString()} results
          </Button>
        </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
